/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomSearchFilterV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { IconButton, InputAdornment, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

function CustomSearchFilterV2({
  searchKeys,
  getDataOnSearch,
  inputData,
  size,
  type,
  variant,
  placeholder,
  valueError,
  isMultiline,
  minRows,
  maxRows,
  disabled,
  txtFldStyle = {},
}) {
  const [searchText, setSearchText] = useState("");

  const handleSearch = () => {
    if (!searchText) {
      getDataOnSearch(inputData);
      return;
    }

    const filteredData = inputData.filter((item) =>
      searchKeys.some((key) =>
        item[key]?.toString().toLowerCase().includes(searchText.toLowerCase()),
      ),
    );

    getDataOnSearch(filteredData);
  };

  useEffect(() => {
    getDataOnSearch(inputData);
  }, [inputData]);

  useEffect(() => {
    handleSearch();
  }, [searchText]);

  const clearSearch = () => setSearchText("");

  return (
    <TextField
      fullWidth
      size={size}
      type={type}
      variant={variant}
      placeholder={placeholder}
      value={searchText}
      error={Boolean(valueError)}
      helperText={valueError}
      multiline={isMultiline}
      minRows={minRows}
      maxRows={maxRows}
      disabled={disabled}
      onChange={(e) => setSearchText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleSearch();
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="action" />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {searchText && (
              <IconButton onClick={clearSearch} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          height: "32px",
          "& fieldset": { borderColor: "#D0D0CE", borderRadius: "0px" }, // Default
          "&:hover fieldset": { borderColor: "#D0D0CE" }, // Hover
          "&.Mui-focused fieldset": { borderColor: "#000000" }, // Focused
          "&.Mui-disabled fieldset": { borderColor: "#D0D0CE" }, // Disabled
          "&.Mui-error fieldset": { borderColor: "#e53935" }, // Error

          "&:hover .MuiInputBase-input::placeholder": {
            color: "#848587", // dark text color on hover
          },

          "&.Mui-focused .MuiInputBase-input::placeholder": {
            color: "#232323", // solid black when focused
          },
        },

        "& .MuiInputLabel-root": {
          color: "#000",
          fontSize: "0.9rem",
          "&.Mui-error": { color: "#e53935" },
          "&.Mui-disabled": { color: "#bdbdbd" },
        },
        "& .MuiInputBase-input::placeholder": {
          color: "#999",
          opacity: 1,
        },
        "& .MuiFormHelperText-root.Mui-error": {
          color: "#e53935",
          fontSize: "0.8rem",
          marginLeft: 0,
          marginTop: "4px",
        },
        ...txtFldStyle,
      }}
    />
  );
}

CustomSearchFilterV2.propTypes = {
  searchKeys: PropTypes.arrayOf(PropTypes.string),
  getDataOnSearch: PropTypes.func,
  inputData: PropTypes.array,
  size: PropTypes.string,
  type: PropTypes.string,
  variant: PropTypes.string,
  placeholder: PropTypes.string,
  valueError: PropTypes.node,
  isMultiline: PropTypes.bool,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  disabled: PropTypes.bool,
  txtFldStyle: PropTypes.object,
};

CustomSearchFilterV2.defaultProps = {
  searchKeys: [],
  getDataOnSearch: () => {},
  inputData: [],
  size: undefined,
  type: undefined,
  variant: undefined,
  placeholder: "",
  valueError: null,
  isMultiline: false,
  minRows: undefined,
  maxRows: undefined,
  disabled: false,
  txtFldStyle: {},
};

export default CustomSearchFilterV2;
